import React, { useCallback, useEffect, useState } from 'react';
import ModalWrapper from 'src/components/common/modal/ModalWrapper';
import { blueButtonClass, defaultValidateDateString, inputFormHeightOption } from 'src/components/common/common';
import { useKSK2021_R01Store } from './Store_KSK2021_R01';
import { showWarning } from 'src/utils/toast/toastUtil';
import { InputForm } from 'src/components/common/form/input/InputForm';
import { KSK2021_R01WorkTimeProcessType } from './Dto_KSK2021_R01';

interface Props {
	handleWorkTimeClick: (productWorkNo: string, processType: KSK2021_R01WorkTimeProcessType, manualTime?: Date, rawTime?: { month: string, day: string, hour: string, minute: string }) => Promise<boolean>;
}

export const WorkTimeModal_KSK2021_R01 = ({
	handleWorkTimeClick
}: Props) => {
	const { 
		workTimeModalVisible, setWorkTimeModalVisible,
		workTimeModalType,
		editKSK2021_R01, setEditKSK2021_R01
	} = useKSK2021_R01Store();

    const [month, setMonth] = useState<string>('');
    const [day, setDay] = useState<string>('');
    const [hour, setHour] = useState<string>('');
    const [minute, setMinute] = useState<string>('');

    useEffect(() => {
        if (workTimeModalVisible) {
            const pad = (n: number) => n.toString().padStart(2, '0');
            const now = new Date();
            // 모달 오픈 시 월·일은 항상 당일로 표기
            setMonth(pad(now.getMonth() + 1));
            setDay(pad(now.getDate()));

            const time = workTimeModalType === 'start' ? editKSK2021_R01?.fromTime : editKSK2021_R01?.toTime;
            const d = time ? new Date(time) : null;
            const isValidExisting =
                !!d &&
                !isNaN(d.getTime()) &&
                d.getTime() !== new Date(defaultValidateDateString).getTime();

            if (isValidExisting) {
                setHour(pad(d.getHours()));
                setMinute(pad(d.getMinutes()));
            } else {
                setHour('');
                setMinute('');
            }
        }
    }, [workTimeModalVisible, workTimeModalType, editKSK2021_R01]);

    const handleModify = async () => {
        if (!month || !day || !hour || !minute) {
            showWarning('시간을 모두 입력해주세요.');
            return;
        }
        
        const year = new Date().getFullYear();

        // const newTime = new Date(year, Number(month) - 1, Number(day), Number(hour) + 9, Number(minute));
		const newTime = new Date(year, Number(month) - 1, Number(day), Number(hour), Number(minute));
        
        const rawTime = { month, day, hour, minute };

		let success = false;
        if (editKSK2021_R01?.productWorkNo) {
            if (workTimeModalType === 'start') {
                success = await handleWorkTimeClick(editKSK2021_R01.productWorkNo, 'workStart', newTime, rawTime);
            } else if (workTimeModalType === 'end') {
                success = await handleWorkTimeClick(editKSK2021_R01.productWorkNo, 'workEnd', newTime, rawTime);
            }
        }
		if (success) {
			setWorkTimeModalVisible(false);
		}
        
    };

	const handleReset = async () => {
		let success = false;
		if (editKSK2021_R01?.productWorkNo) {
			if (workTimeModalType === 'start') {
				success = await handleWorkTimeClick(editKSK2021_R01.productWorkNo, 'workStartReset');
			} else if (workTimeModalType === 'end') {
				success = await handleWorkTimeClick(editKSK2021_R01.productWorkNo, 'workEndReset');
			}
		}
		if (success) {
			setWorkTimeModalVisible(false);
		}
	}

	const handleMinuteKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			void handleModify();
		}
	}, [month, day, hour, minute, workTimeModalType, editKSK2021_R01, handleWorkTimeClick, setWorkTimeModalVisible]);

	
	const wrapperOption = {
		wrapperStyle: "w-[50px] min-w-[50px] mb-[4px]",
		titleStyle: "w-[150px] min-w-[150px] text-left",
		inputStyle: "w-[50px] min-w-[50px] " + inputFormHeightOption,
	}

    if (!workTimeModalVisible) return null;

    return (
        <ModalWrapper
			size={'hxs'}
            defaultWidth={500}
            overlay={true}
            onOverlayClick={() => setWorkTimeModalVisible(false)}
            onEscKeyDown={() => setWorkTimeModalVisible(false)}
        >
            <div className="w-full h-[20%] min-h-[20%] flex items-center px-[24px] bg-gray-100">
                <p className="font-bold">
                    {workTimeModalType === 'start' ? '작업 시작시간 수정' : '작업 종료시간 수정'}
                </p>
            </div>
			<div className="w-full h-[40%] min-h-[40%] flex px-[24px] py-[24px] gap-[8px] justify-center items-center">
                <InputForm 
                    title="월" 
					name="month"
                    value={month} 
                    onChange={(e) => setMonth(e.target.value)} 
                    editable={true} 
                    maxLength={2}
                    wrapperOption={wrapperOption}
					titleless={true}
					dragSelectAll={true}
                />
				월
                <InputForm 
                    title="일" 
					name="day"
                    value={day} 
                    onChange={(e) => setDay(e.target.value)} 
                    editable={true} 
                    maxLength={2}
                    wrapperOption={wrapperOption}
					titleless={true}
					dragSelectAll={true}
                />
				일		
                <InputForm 
                    title="시" 
					name="hour"
                    value={hour} 
                    onChange={(e) => setHour(e.target.value)} 
                    editable={true} 
                    maxLength={2}
                    wrapperOption={wrapperOption}
					titleless={true}
					dragSelectAll={true}
                />
				시
                <InputForm 
                    title="분" 
					name="minute"
                    value={minute} 
                    onChange={(e) => setMinute(e.target.value)} 
                    editable={true} 
                    maxLength={2}
                    wrapperOption={wrapperOption}
					titleless={true}
					dragSelectAll={true}
					onKeyDown={handleMinuteKeyDown}
                />
				분
			</div>
            <div className="w-full h-[40%] min-h-[40%] flex items-center justify-end px-[24px] gap-[8px]">
				<button
                    className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4 rounded shadow h-[30px]`}
                    onClick={handleReset}
                >
                    초기화
                </button>
                <button
                    className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4 rounded shadow h-[30px]`}
                    onClick={handleModify}
                >
                    수정
                </button>
                <button
                    className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4 rounded shadow h-[30px]`}
                    onClick={() => setWorkTimeModalVisible(false)}
                >
                    취소
                </button>
            </div>
        </ModalWrapper>
    );
};

